import { keepPreviousData, queryOptions } from '@tanstack/react-query'

import type { DualControlResult, PagedResult } from './types'
import type {
  AssignFieldTillRequest,
  CashCount,
  CashCountPayload,
  CashMovementRequest,
  CloseTellerSessionRequest,
  CounterAccountSearchQuery,
  CounterLoanAccount,
  CounterLoanAccountPayload,
  CounterLoanCycle,
  CounterLoanCyclePayload,
  CounterLoanSearchQuery,
  CounterRepaymentPosted,
  CounterRepaymentPostedPayload,
  CounterRepaymentRequest,
  CounterSavingsAccount,
  CounterSavingsAccountPayload,
  CounterSavingsMovementRequest,
  CounterSavingsPosting,
  CounterSavingsPostingPayload,
  CounterSavingsProduct,
  CounterSavingsProductPayload,
  CreateTillRequest,
  CreateVaultRequest,
  OpenTellerSessionRequest,
  RecordCashCountRequest,
  TellerSession,
  TellerSessionListQuery,
  TellerSessionPayload,
  Till,
  TillDetail,
  TillDetailPayload,
  TillListQuery,
  TillMovement,
  TillMovementPayload,
  TillPayload,
  Vault,
  VaultPayload,
  WireInteger,
} from './teller-types'

import { apiClient } from './client'

/**
 * The tellering endpoints, and the four counter rails that post cash through them.
 *
 * Everything the API sends is narrowed here rather than downstream. Two conversions happen at this
 * boundary and nowhere else:
 *
 *   - `int64` arrives as a JSON number *or* as the same integer written as a string, which is how
 *     this API serialises 64-bit values. A `MoneyDisplay` handed `"250000"` renders nothing useful
 *     and a comparison against it is a string comparison, so every amount is coerced once, here.
 *   - branch identifiers arrive as numbers on some payloads and strings on others. This application
 *     uses strings everywhere — session storage, the `X-Branch-Id` header — so they are normalised
 *     the way `reference-api.ts` normalises them for branches, and converted back on the two
 *     request bodies that want a number.
 *
 * Nothing here formats money, and nothing here decides what a user may do: the server re-checks
 * every permission, and a 403 arrives as an `ApiError` the screens surface.
 */

/** An `int64` from the wire, as a number. */
const int = (value: WireInteger): number => (typeof value === 'number' ? value : Number(value))

/** The same, where the API may legitimately send null — an unset limit, an undeclared drawer. */
const optionalInt = (value: WireInteger | null | undefined): number | null =>
  value === null || value === undefined ? null : int(value)

/** Every identifier this application handles is a string, whatever the payload says. */
const id = (value: WireInteger): string => String(value)

/**
 * The numeric form the two `int64` branch parameters want back. `Number` rather than a cast: a
 * branch identifier that is not a number is a bug worth failing on rather than sending as `NaN`.
 */
const branchNumber = (value: string): number => Number(value)

/* ─── Normalisers ────────────────────────────────────────────────────────────────────────────*/

const toVault = (payload: VaultPayload): Vault => ({
  ...payload,
  branchId: id(payload.branchId),
  cashOnHandMinor: int(payload.cashOnHandMinor),
})

const toTill = (payload: TillPayload): Till => ({
  ...payload,
  branchId: id(payload.branchId),
  cashOnHandMinor: int(payload.cashOnHandMinor),
  cashHoldingLimitMinor: optionalInt(payload.cashHoldingLimitMinor),
})

const toMovement = (payload: TillMovementPayload): TillMovement => ({
  ...payload,
  amountMinor: int(payload.amountMinor),
  balanceAfterMinor: int(payload.balanceAfterMinor),
})

const toCashCount = (payload: CashCountPayload): CashCount => ({
  ...payload,
  totalMinor: int(payload.totalMinor),
  lines: payload.lines.map((line) => ({
    denominationMinor: int(line.denominationMinor),
    pieces: int(line.pieces),
    subtotalMinor: int(line.subtotalMinor),
  })),
})

const toSession = (payload: TellerSessionPayload): TellerSession => ({
  ...payload,
  branchId: id(payload.branchId),
  openingFloatMinor: int(payload.openingFloatMinor),
  cashInMinor: int(payload.cashInMinor),
  cashOutMinor: int(payload.cashOutMinor),
  expectedClosingCashMinor: int(payload.expectedClosingCashMinor),
  declaredClosingCashMinor: optionalInt(payload.declaredClosingCashMinor),
  overShortMinor: optionalInt(payload.overShortMinor),
  // Null is carried through as null. A drawer nobody has counted is not a drawer counted at zero.
  latestCount: payload.latestCount ? toCashCount(payload.latestCount) : null,
})

const toSavingsAccount = (payload: CounterSavingsAccountPayload): CounterSavingsAccount => ({
  ...payload,
  branchId: id(payload.branchId),
  ledgerBalanceMinor: int(payload.ledgerBalanceMinor),
  holdAmountMinor: int(payload.holdAmountMinor),
  availableBalanceMinor: int(payload.availableBalanceMinor),
  accruedInterestMinor: int(payload.accruedInterestMinor),
  holds: payload.holds.map((hold) => ({ ...hold, amountMinor: int(hold.amountMinor) })),
})

const toSavingsPosting = (payload: CounterSavingsPostingPayload): CounterSavingsPosting => ({
  ...payload,
  balanceAfterMinor: int(payload.balanceAfterMinor),
  availableBalanceMinor: int(payload.availableBalanceMinor),
  transaction: {
    ...payload.transaction,
    amountMinor: int(payload.transaction.amountMinor),
    balanceAfterMinor: int(payload.transaction.balanceAfterMinor),
  },
})

const toSavingsProduct = (payload: CounterSavingsProductPayload): CounterSavingsProduct => ({
  ...payload,
  minBalanceMinor: int(payload.minBalanceMinor),
  withdrawalFeeMinor: optionalInt(payload.withdrawalFeeMinor),
})

const toLoanCycle = (payload: CounterLoanCyclePayload): CounterLoanCycle => ({
  ...payload,
  cycleNumber: int(payload.cycleNumber),
  principalMinor: int(payload.principalMinor),
  principalOutstandingMinor: int(payload.principalOutstandingMinor),
  interestOutstandingMinor: int(payload.interestOutstandingMinor),
  penaltyOutstandingMinor: int(payload.penaltyOutstandingMinor),
  feesOutstandingMinor: int(payload.feesOutstandingMinor),
  totalOutstandingMinor: int(payload.totalOutstandingMinor),
  daysInArrears: int(payload.daysInArrears),
  amountInArrearsMinor: int(payload.amountInArrearsMinor),
})

const toLoanAccount = (payload: CounterLoanAccountPayload): CounterLoanAccount => ({
  ...payload,
  branchId: id(payload.branchId),
  totalCyclesCompleted: int(payload.totalCyclesCompleted),
  currentCycle: payload.currentCycle ? toLoanCycle(payload.currentCycle) : null,
})

const toRepaymentPosted = (payload: CounterRepaymentPostedPayload): CounterRepaymentPosted => ({
  ...payload,
  amountMinor: int(payload.amountMinor),
  allocation: {
    penaltyMinor: int(payload.allocation.penaltyMinor),
    feeMinor: int(payload.allocation.feeMinor),
    interestMinor: int(payload.allocation.interestMinor),
    principalMinor: int(payload.allocation.principalMinor),
    lines: payload.allocation.lines.map((line) => ({
      instalmentNumber: int(line.instalmentNumber),
      bucket: line.bucket,
      amountMinor: int(line.amountMinor),
    })),
  },
  cycle: toLoanCycle(payload.cycle),
})

/** Pages arrive with `int32` counts under the same number-or-string union as everything else. */
function toPage<TPayload, TItem>(
  page: PagedResult<TPayload>,
  map: (item: TPayload) => TItem,
): PagedResult<TItem> {
  return {
    items: (page.items ?? []).map(map),
    page: int(page.page),
    pageSize: int(page.pageSize),
    totalCount: int(page.totalCount),
    totalPages: int(page.totalPages),
  }
}

/* ─── Query keys ─────────────────────────────────────────────────────────────────────────────
 *
 * The list keys carry their whole query object, so changing a filter is a different cache entry
 * rather than a mutation of one — which is what keeps a page of results on screen while the next
 * is fetched, and makes "invalidate every till" a single prefix match after a cash movement.
 */
export const tellerKeys = {
  all: ['teller'] as const,
  tills: () => [...tellerKeys.all, 'tills'] as const,
  tillList: (query: TillListQuery) => [...tellerKeys.tills(), 'list', query] as const,
  till: (tillId: string, businessDate: string | null) =>
    [...tellerKeys.tills(), 'detail', tillId, businessDate] as const,
  vaults: (branchId: string | null) => [...tellerKeys.all, 'vaults', branchId] as const,
  sessions: () => [...tellerKeys.all, 'sessions'] as const,
  sessionList: (query: TellerSessionListQuery) => [...tellerKeys.sessions(), 'list', query] as const,
  session: (sessionId: string) => [...tellerKeys.sessions(), 'detail', sessionId] as const,
  counter: () => [...tellerKeys.all, 'counter'] as const,
  savingsAccounts: (query: CounterAccountSearchQuery) =>
    [...tellerKeys.counter(), 'savings-accounts', query] as const,
  savingsAccount: (accountId: string) => [...tellerKeys.counter(), 'savings-account', accountId] as const,
  savingsProducts: () => [...tellerKeys.counter(), 'savings-products'] as const,
  loanAccounts: (query: CounterLoanSearchQuery) => [...tellerKeys.counter(), 'loan-accounts', query] as const,
}

/* ─── Reads ──────────────────────────────────────────────────────────────────────────────────*/

/**
 * Vault cash positions. Gated on `teller.vault.view`, which the shipped Teller role does **not**
 * hold — so a 403 here is ordinary rather than exceptional, and the screen says which permission
 * is missing instead of showing an empty branch.
 */
export const vaultsQueryOptions = (branchId: string | null) =>
  queryOptions({
    queryKey: tellerKeys.vaults(branchId),
    queryFn: async () => {
      const response = await apiClient.get<VaultPayload[]>('/teller/vaults', {
        params: branchId ? { branchId: branchNumber(branchId) } : undefined,
      })

      // A bare array, unlike every other list on this module. Defended anyway: the header renders
      // above this screen and a reference lookup answering oddly should cost a panel, not a page.
      return Array.isArray(response.data) ? response.data.map(toVault) : []
    },
    staleTime: 30 * 1000,
  })

/** Till cash positions, counter and field. Gated on `teller.till.view`. */
export const tillListQueryOptions = (query: TillListQuery) =>
  queryOptions({
    queryKey: tellerKeys.tillList(query),
    queryFn: async () => {
      const response = await apiClient.get<PagedResult<TillPayload>>('/teller/tills', {
        params: {
          ...query,
          branchId: query.branchId ? branchNumber(query.branchId) : undefined,
        },
      })

      return toPage(response.data, toTill)
    },
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  })

/**
 * One drawer and the movements behind its balance.
 *
 * `businessDate` is the ledger's date and is passed through as given — including as `undefined`,
 * which asks the server for its own current day. It is never defaulted from the browser's clock.
 */
export const tillQueryOptions = (tillId: string, businessDate: string | null) =>
  queryOptions({
    queryKey: tellerKeys.till(tillId, businessDate),
    queryFn: async () => {
      const response = await apiClient.get<TillDetailPayload>(
        `/teller/tills/${encodeURIComponent(tillId)}`,
        { params: businessDate ? { businessDate } : undefined },
      )

      return {
        till: toTill(response.data.till),
        movements: (response.data.movements ?? []).map(toMovement),
      } satisfies TillDetail
    },
    staleTime: 15 * 1000,
  })

/** Teller shifts, for the day-close checklist. Gated on `teller.till.view`. */
export const tellerSessionListQueryOptions = (query: TellerSessionListQuery) =>
  queryOptions({
    queryKey: tellerKeys.sessionList(query),
    queryFn: async () => {
      const response = await apiClient.get<PagedResult<TellerSessionPayload>>('/teller/sessions', {
        params: query,
      })

      return toPage(response.data, toSession)
    },
    placeholderData: keepPreviousData,
    staleTime: 15 * 1000,
  })

export const tellerSessionQueryOptions = (sessionId: string) =>
  queryOptions({
    queryKey: tellerKeys.session(sessionId),
    queryFn: async () => {
      const response = await apiClient.get<TellerSessionPayload>(
        `/teller/sessions/${encodeURIComponent(sessionId)}`,
      )

      return toSession(response.data)
    },
    // A drawer's cash-in and cash-out change with every posting made at the counter, and the
    // expected closing figure is read while money is being counted against it.
    staleTime: 0,
  })

/* ─── Writes ─────────────────────────────────────────────────────────────────────────────────*/

/** Opens a shift. Returns the session itself — this one is not dual-controlled. */
export async function openTellerSession(request: OpenTellerSessionRequest): Promise<TellerSession> {
  const response = await apiClient.post<TellerSessionPayload>('/teller/sessions', request)
  return toSession(response.data)
}

/**
 * Declares the drawer's closing cash.
 *
 * Returns {@link DualControlResult}, not a closed session: under the shipped policy this is queued
 * for a supervisor's countersignature and the session sits in `PendingClose` until then. The screen
 * must say "sent for countersignature", never "closed".
 */
export async function closeTellerSession(
  sessionId: string,
  request: CloseTellerSessionRequest,
): Promise<DualControlResult> {
  const response = await apiClient.post<DualControlResult>(
    `/teller/sessions/${encodeURIComponent(sessionId)}/close`,
    request,
  )

  return response.data
}

/** Records a denomination count. Immediate — a count is evidence, not a change to any balance. */
export async function recordCashCount(
  sessionId: string,
  request: RecordCashCountRequest,
): Promise<CashCount> {
  const response = await apiClient.post<CashCountPayload>(
    `/teller/sessions/${encodeURIComponent(sessionId)}/counts`,
    request,
  )

  return toCashCount(response.data)
}

/**
 * Moves cash between the vault, a till, and the bank.
 *
 * The approval gate is asked *before* any balance is checked, so the refusals this form might seem
 * to be guarding against — `teller.vault_insufficient_cash`, `teller.till_insufficient_cash`,
 * `teller.till_cash_limit_exceeded` — cannot arrive here while a threshold defers the movement.
 * They surface to the approver instead, hours later. The dialog says so.
 */
export async function moveCash(request: CashMovementRequest): Promise<DualControlResult> {
  const response = await apiClient.post<DualControlResult>('/teller/cash-movements', {
    ...request,
    branchId: branchNumber(request.branchId),
  })

  return response.data
}

/**
 * Creates a till. Dual-controlled, but `teller.till_number_taken` is checked *before* the gate, so
 * a duplicate number is refused immediately rather than at countersignature.
 */
export async function createTill(request: CreateTillRequest): Promise<DualControlResult> {
  const response = await apiClient.post<DualControlResult>('/teller/tills', {
    ...request,
    branchId: branchNumber(request.branchId),
  })

  return response.data
}

/** Creates a branch vault. As above: `teller.vault_already_exists` is immediate. */
export async function createVault(request: CreateVaultRequest): Promise<DualControlResult> {
  const response = await apiClient.post<DualControlResult>('/teller/vaults', {
    ...request,
    branchId: branchNumber(request.branchId),
  })

  return response.data
}

/**
 * Puts a field till in a loan officer's hands for one day. Answers 204 and nothing else, so the
 * caller refetches the till.
 */
export async function assignFieldTill(tillId: string, request: AssignFieldTillRequest): Promise<void> {
  await apiClient.post(`/teller/tills/${encodeURIComponent(tillId)}/assign`, request)
}

/* ─── The counter's transaction rails ────────────────────────────────────────────────────────*/

/**
 * The savings accounts a teller can post against.
 *
 * `productClass` is worth filtering on rather than sorting afterwards, because the two classes take
 * cash through different endpoints: a `SecurityDeposit` account is refused by the deposit endpoint
 * outright (`savings.account_wrong_class`) and has its own top-up rail.
 */
export const counterSavingsAccountsQueryOptions = (query: CounterAccountSearchQuery, enabled: boolean) =>
  queryOptions({
    queryKey: tellerKeys.savingsAccounts(query),
    queryFn: async () => {
      const response = await apiClient.get<PagedResult<CounterSavingsAccountPayload>>('/savings/accounts', {
        params: {
          ...query,
          branchId: query.branchId ? branchNumber(query.branchId) : undefined,
        },
      })

      return toPage(response.data, toSavingsAccount)
    },
    enabled,
    placeholderData: keepPreviousData,
  })

/** One account, refetched after every posting so the balance on screen is the posted one. */
export const counterSavingsAccountQueryOptions = (accountId: string | null) =>
  queryOptions({
    queryKey: tellerKeys.savingsAccount(accountId ?? ''),
    queryFn: async () => {
      const response = await apiClient.get<CounterSavingsAccountPayload>(
        `/savings/accounts/${encodeURIComponent(accountId ?? '')}`,
      )

      return toSavingsAccount(response.data)
    },
    enabled: Boolean(accountId),
    staleTime: 0,
  })

/**
 * The savings products, read only to show a withdrawal's fee and minimum balance.
 *
 * Gated on `savings.product.view`, which the shipped Teller role does not hold — so `enabled` is
 * passed by the caller from the permission check and the withdrawal rail states the two figures are
 * unavailable rather than pretending the available balance is the whole answer.
 */
export const counterSavingsProductsQueryOptions = (enabled: boolean) =>
  queryOptions({
    queryKey: tellerKeys.savingsProducts(),
    queryFn: async () => {
      const response = await apiClient.get<CounterSavingsProductPayload[] | PagedResult<CounterSavingsProductPayload>>(
        '/savings/products',
      )

      const payload = response.data
      const items = Array.isArray(payload) ? payload : (payload.items ?? [])
      return items.map(toSavingsProduct)
    },
    enabled,
    staleTime: 30 * 60 * 1000,
  })

/** Loan accounts a collection can be posted against. Gated on `loans.view`. */
export const counterLoanAccountsQueryOptions = (query: CounterLoanSearchQuery, enabled: boolean) =>
  queryOptions({
    queryKey: tellerKeys.loanAccounts(query),
    queryFn: async () => {
      const response = await apiClient.get<PagedResult<CounterLoanAccountPayload>>('/loan-accounts', {
        params: {
          ...query,
          branchId: query.branchId ? branchNumber(query.branchId) : undefined,
        },
      })

      return toPage(response.data, toLoanAccount)
    },
    enabled,
    placeholderData: keepPreviousData,
  })

/**
 * Takes cash into a savings account. Immediate — a deposit is not dual-controlled.
 *
 * Refuses a security deposit account with `savings.account_wrong_class` before doing anything, which
 * is why the counter routes that class to {@link topUpSecurityDeposit} instead.
 */
export async function postSavingsDeposit(
  accountId: string,
  request: CounterSavingsMovementRequest,
): Promise<CounterSavingsPosting> {
  const response = await apiClient.post<CounterSavingsPostingPayload>(
    `/savings/accounts/${encodeURIComponent(accountId)}/deposits`,
    request,
  )

  return toSavingsPosting(response.data)
}

/**
 * Takes cash collateral into a security deposit account. The fifth counter cash-in operation, and
 * the only one that account class accepts. Needs `savings.security_deposit.topup`.
 */
export async function topUpSecurityDeposit(
  accountId: string,
  request: CounterSavingsMovementRequest,
): Promise<CounterSavingsPosting> {
  const response = await apiClient.post<CounterSavingsPostingPayload>(
    `/savings/accounts/${encodeURIComponent(accountId)}/security-deposit`,
    request,
  )

  return toSavingsPosting(response.data)
}

/**
 * Pays savings out over the counter.
 *
 * Returns {@link DualControlResult}. Two things follow from that and both are said on the screen:
 * the money has not moved when this answers `awaitingApproval`, and the custody checks — including
 * `teller.no_open_till` and the drawer's own permission check — run *after* the gate, so a teller
 * with no open session can be told "sent for countersignature" and have the movement fail at
 * approval time.
 */
export async function postSavingsWithdrawal(
  accountId: string,
  request: CounterSavingsMovementRequest,
): Promise<DualControlResult> {
  const response = await apiClient.post<DualControlResult>(
    `/savings/accounts/${encodeURIComponent(accountId)}/withdrawals`,
    request,
  )

  return response.data
}

/**
 * Posts a collection against a loan round. Immediate, and the split across penalty, fee, interest
 * and principal is computed by the server — this client never allocates a payment.
 */
export async function postLoanRepayment(
  cycleId: string,
  request: CounterRepaymentRequest,
): Promise<CounterRepaymentPosted> {
  const response = await apiClient.post<CounterRepaymentPostedPayload>(
    `/loan-cycles/${encodeURIComponent(cycleId)}/repayments`,
    request,
  )

  return toRepaymentPosted(response.data)
}
